import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import './Game.css';

const PEARLS = [
  { id: 0, label: 'Maman\ncourageuse', gradient: 'radial-gradient(circle at 35% 30%, #ff9999 0%, #dc2626 50%, #7f1d1d 100%)' },
  { id: 1, label: 'Maman\ntendre',     gradient: 'radial-gradient(circle at 35% 30%, #f9a8d4 0%, #ec4899 50%, #831843 100%)' },
  { id: 2, label: 'Maman\nsagesse',    gradient: 'radial-gradient(circle at 35% 30%, #bfdbfe 0%, #1d4ed8 50%, #1e3a8a 100%)' },
  { id: 3, label: 'Maman\ngénéreuse',  gradient: 'radial-gradient(circle at 35% 30%, #bbf7d0 0%, #16a34a 50%, #14532d 100%)' },
  { id: 4, label: 'Maman\nprécieuse',  gradient: 'radial-gradient(circle at 35% 30%, #fef08a 0%, #d97706 50%, #78350f 100%)' },
  { id: 5, label: 'Maman\nroyale',     gradient: 'radial-gradient(circle at 35% 30%, #ddd6fe 0%, #7c3aed 50%, #4c1d95 100%)' },
  { id: 6, label: 'Maman\nrayonnante', gradient: 'radial-gradient(circle at 35% 30%, #fecdd3 0%, #f43f5e 50%, #881337 100%)' },
  { id: 7, label: 'Maman\néternelle',  gradient: 'radial-gradient(circle at 35% 30%, #fed7aa 0%, #ea580c 50%, #7c2d12 100%)' },
];

const STEPS = ['Achat', 'Enregistrement', 'Perle', 'Résultat'];

const PRIZE_IMAGES = {
  sm_air_fryer: '/images/air-fryer.png',
  sm_pagne:     '/images/pagne-des-meres.png',
  sm_mug:       '/images/mug.png',
  sm_serviette: '/images/serviette.png',
  sm_tshirt:    '/images/t-shirt.png',
  sm_pack:      '/images/pack-supermont.png',
  sm_bouteille: '/images/bouteille-supermont.png',
};

const getPrizeImage = (prize) => prize?.icon || PRIZE_IMAGES[prize?.prize_type] || null;

const isTryAgain = (prize) =>
  !prize?.prize_type ||
  prize.prize_type.toLowerCase().includes('try_again') ||
  prize.prize_type.toLowerCase().includes('reessay');

const ProgressBar = ({ activeStep }) => (
  <div className="progress-bar-wrap">
    <div className="progress-steps">
      {STEPS.map((label, i) => (
        <>
          {i > 0 && <div key={`line-${i}`} className={`step-line${i < activeStep ? ' done' : ''}`} />}
          <div key={label} className="step">
            <div className={`step-circle${i + 1 < activeStep ? ' done' : ''}${i + 1 === activeStep ? ' active' : ''}`}>
              {i + 1 < activeStep ? <i className="fa fa-check" style={{ fontSize: '12px' }} /> : i + 1}
            </div>
            <span className={`step-label${i + 1 === activeStep ? ' active' : ''}`}>{label}</span>
          </div>
        </>
      ))}
    </div>
  </div>
);

const Header = ({ activeStep }) => (
  <div className="game-header">
    <div className="brand-row">
      <div className="brand-logo"><img src="/images/logo-supermont.png" alt="Supermont" style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: "50%" }} /></div>
      <span className="brand-name">Supermont</span>
    </div>
    <div className="game-title">Les perles de vie</div>
    <ProgressBar activeStep={activeStep} />
  </div>
);

export default function Game() {
  const { token } = useParams();
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [allPrizes, setAllPrizes]       = useState([]);
  const [prize, setPrize]               = useState(null);
  const [playNumber, setPlayNumber]     = useState(null);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [selectedPearl, setSelectedPearl] = useState(null);
  const [phase, setPhase]               = useState('select'); // 'select' | 'flipping' | 'result'
  const [isFlipped, setIsFlipped]       = useState(false);
  const [revealedPrizes, setRevealedPrizes] = useState([]); // historique local des tours joués

  const verifyToken = useCallback(async () => {
    try {
      const res = await api.get(`/game/verify/${token}`);
      if (!res.data.valid) {
        setError(res.data.error || 'Lien invalide ou expiré');
        return;
      }
      const prizes = res.data.predetermined_prizes || [];
      setAllPrizes(prizes);
      const alreadyRevealed = prizes.filter(p => p.revealed);
      setRevealedPrizes(alreadyRevealed);
      const unrevealed = prizes.find(p => !p.revealed);
      if (unrevealed) {
        setPrize(unrevealed);
        setPlayNumber(unrevealed.play_number);
      } else if (prizes.length > 0) {
        setPrize(prizes[prizes.length - 1]);
        setAlreadyPlayed(true);
        setPhase('result');
      } else {
        setError('Aucun tirage disponible pour ce lien');
      }
    } catch {
      setError('Impossible de valider le lien. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { verifyToken(); }, [verifyToken]);

  const handleFlip = async () => {
    if (selectedPearl === null) return;

    setPhase('flipping');
    try {
      await api.post(`/game/mark-played/${token}`, { play_number: playNumber });
    } catch {
      // non-blocking
    }
    setTimeout(() => setIsFlipped(true), 200);
    setTimeout(() => setPhase('result'), 1500);
  };

  const handleNextTurn = () => {
    // Marquer le prix actuel comme révélé localement
    const nowRevealed = [...revealedPrizes, prize];
    setRevealedPrizes(nowRevealed);
    // Trouver le prochain non-révélé
    const nextUnrevealed = allPrizes.find(p => !nowRevealed.some(r => r.play_number === p.play_number));
    if (nextUnrevealed) {
      setPrize(nextUnrevealed);
      setPlayNumber(nextUnrevealed.play_number);
      setSelectedPearl(null);
      setIsFlipped(false);
      setPhase('select');
    }
  };

  const totalTours = allPrizes.length;
  const tourActuel = revealedPrizes.length + (phase === 'result' ? 1 : 0);
  const hasMoreTurns = phase === 'result' && tourActuel < totalTours;

  useEffect(() => {
    if (phase !== 'result') return;
    if (isTryAgain(prize)) return;
    import('canvas-confetti').then(m => {
      m.default({ particleCount: 180, spread: 80, origin: { y: 0.55 },
        colors: ['#1565c0', '#42a5f5', '#e3f2fd', '#ffffff', '#fce4ec'] });
      setTimeout(() => m.default({ particleCount: 80, spread: 50, origin: { y: 0.7, x: 0.2 },
        colors: ['#1565c0', '#42a5f5', '#ffffff'] }), 400);
      setTimeout(() => m.default({ particleCount: 80, spread: 50, origin: { y: 0.7, x: 0.8 },
        colors: ['#1565c0', '#42a5f5', '#ffffff'] }), 700);
    });
  }, [phase]);

  /* ---- Loading ---- */
  if (loading) return (
    <div className="center-screen">
      <div className="spinner" />
      <p style={{ color: '#1565c0', fontWeight: 700 }}>Chargement…</p>
    </div>
  );

  /* ---- Error ---- */
  if (error) return (
    <div className="center-screen">
      <i className="fa fa-times-circle error-icon" />
      <p className="error-title">Lien invalide</p>
      <p className="error-msg">{error}</p>
    </div>
  );

  /* ---- Flipping phase ---- */
  if (phase === 'flipping') {
    const pearl = PEARLS[selectedPearl];
    return (
      <div className="game-wrapper">
        <Header activeStep={3} />
        <div className="game-content flipping-stage">
          <p className="game-instruction">Retournement en cours…</p>
          <div className="flipping-pearl-wrap">
            <div className={`flipping-pearl-inner${isFlipped ? ' flipped' : ''}`}>
              <div className="flipping-front" style={{ background: pearl.gradient }} />
              <div className="flipping-back">
                {isTryAgain(prize)
                  ? <><i className="fa fa-rotate-right" /><span style={{ fontSize: '11px', fontWeight: 800, color: '#1565c0' }}>Réessayez</span></>
                  : <i className="fa fa-gift" />
                }
              </div>
            </div>
          </div>
          <p className="flipping-text">
            <i className="fa fa-circle-notch fa-spin me-2" />
            Découvrez votre lot…
          </p>
        </div>
      </div>
    );
  }

  /* ---- Result phase ---- */
  if (phase === 'result') {
    const won = !isTryAgain(prize);
    return (
      <div className="game-wrapper">
        <Header activeStep={4} />
        <div className="game-content result-screen">
          <div className={`result-card pop-in ${won ? 'winner' : 'loser'}`}>

            {won ? (
              <>
                <span className="result-badge win-badge">
                  <i className="fa fa-star me-1" />Félicitations !
                </span>

                {getPrizeImage(prize) ? (
                  <img src={getPrizeImage(prize)} alt={prize.prize_label} className="result-prize-img" />
                ) : (
                  <div className="result-icon">
                    <i className="fa fa-gift" />
                  </div>
                )}

                <p className="result-prize-name">{prize?.prize_label || 'Lot gagné'}</p>
                {prize?.prize_name && prize.prize_name !== prize.prize_label && (
                  <p style={{ color: '#64b5f6', fontSize: '13px', fontWeight: 600 }}>{prize.prize_name}</p>
                )}

                <p className="result-congrats">
                  Bravo ! Présentez ce message à l'hôtesse<br />pour récupérer votre lot.
                </p>
              </>
            ) : (
              <>
                <span className="result-badge lose-badge">
                  <i className="fa fa-rotate-right me-1" />Réessayez encore
                </span>

                <div className="result-icon loser-icon">
                  <i className="fa fa-water" />
                </div>

                <p className="result-prize-name" style={{ color: '#42a5f5' }}>
                  Pas de chance cette fois…
                </p>

                <p className="result-loser-msg">
                  Merci d'avoir participé à<br />
                  <strong>Les Perles de Vie Supermont</strong> !
                </p>
              </>
            )}
          </div>

          {totalTours > 1 && (
            <p style={{ color: '#888', fontSize: '13px', marginTop: '8px' }}>
              Tour {tourActuel} / {totalTours}
            </p>
          )}

          {hasMoreTurns ? (
            <button
              onClick={handleNextTurn}
              style={{
                marginTop: '16px',
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(21,101,192,0.3)'
              }}
            >
              <i className="fa fa-gem me-2" />
              Tour suivant ({tourActuel + 1}/{totalTours})
            </button>
          ) : (
            <>
              {totalTours > 1 && (() => {
                const allRevealedNow = [...revealedPrizes, prize];
                const wonCount = allRevealedNow.filter(p => !isTryAgain(p)).length;
                return (
                  <div style={{ width: '100%', marginTop: '24px' }}>
                    <p style={{ fontWeight: 800, color: '#1565c0', fontSize: '15px', marginBottom: '12px', textAlign: 'center' }}>
                      <i className="fa fa-list me-2" />Récapitulatif de vos tours
                    </p>
                    {allRevealedNow.map((p, i) => {
                      const isWon = !isTryAgain(p);
                      return (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: '12px',
                          padding: '10px 14px',
                          background: isWon ? '#e3f2fd' : '#f5f5f5',
                          borderRadius: '10px', marginBottom: '8px',
                          border: `1px solid ${isWon ? '#90caf9' : '#e0e0e0'}`
                        }}>
                          <div style={{
                            width: '32px', height: '32px', borderRadius: '50%',
                            background: isWon ? '#1565c0' : '#bdbdbd',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                          }}>
                            <i className={`fa ${isWon ? 'fa-gift' : 'fa-rotate-right'}`} style={{ color: 'white', fontSize: '14px' }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <p style={{ margin: 0, fontWeight: 700, fontSize: '14px', color: isWon ? '#0d47a1' : '#757575' }}>
                              Tour {i + 1}
                            </p>
                            <p style={{ margin: 0, fontSize: '12px', color: isWon ? '#1565c0' : '#9e9e9e' }}>
                              {isWon ? (p.prize_label || 'Lot gagné') : 'Pas de chance'}
                            </p>
                          </div>
                          {isWon && getPrizeImage(p) && (
                            <img src={getPrizeImage(p)} alt={p.prize_label} style={{ width: '64px', height: '64px', objectFit: 'contain', borderRadius: '8px' }} />
                          )}
                          <span style={{
                            fontSize: '11px', fontWeight: 800, padding: '3px 8px', borderRadius: '20px',
                            background: isWon ? '#1565c0' : '#e0e0e0',
                            color: isWon ? 'white' : '#757575'
                          }}>
                            {isWon ? 'Gagné ✓' : 'Raté'}
                          </span>
                        </div>
                      );
                    })}
                    <p style={{ textAlign: 'center', marginTop: '12px', fontWeight: 700, color: '#1565c0', fontSize: '14px' }}>
                      <i className="fa fa-trophy me-1" />
                      {wonCount > 0
                        ? `${wonCount} lot${wonCount > 1 ? 's' : ''} gagné${wonCount > 1 ? 's' : ''} sur ${totalTours} tour${totalTours > 1 ? 's' : ''}`
                        : `Aucun lot gagné sur ${totalTours} tours`}
                    </p>
                  </div>
                );
              })()}
              {!alreadyPlayed && (
                <p className="result-footer">
                  <i className="fa fa-info-circle me-1" />
                  Participation enregistrée le {new Date().toLocaleDateString('fr-FR')}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  /* ---- Select phase ---- */
  return (
    <div className="game-wrapper">
      <Header activeStep={3} />
      <div className="game-content">
        <p className="game-instruction">
          Choisissez la perle de votre maman
        </p>

        {totalTours > 1 && (
          <p style={{ color: '#888', fontSize: '13px', marginBottom: '8px' }}>
            Tour {tourActuel > 0 ? tourActuel : revealedPrizes.length + 1} / {totalTours}
          </p>
        )}

        <div className="pearl-grid">
          {PEARLS.map(pearl => {
            const isSelected = selectedPearl === pearl.id;
            const othersSelected = selectedPearl !== null && !isSelected;
            return (
              <div key={pearl.id} className="pearl-item">
                <div className="pearl-container">
                  <div className="pearl-inner">
                    <div
                      className={`pearl-front${isSelected ? ' selected' : ''}${othersSelected ? ' others-selected' : ''}`}
                      style={{ background: pearl.gradient }}
                      onClick={() => setSelectedPearl(pearl.id)}
                    />
                  </div>
                </div>
                <span className="pearl-label" style={{ whiteSpace: 'pre-line' }}>
                  {pearl.label}
                </span>
              </div>
            );
          })}
        </div>

        <button
          className="flip-btn"
          disabled={selectedPearl === null}
          onClick={handleFlip}
        >
          <i className="fa fa-rotate" />
          Retourner la perle
        </button>
      </div>
    </div>
  );
}
